import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updatePassword as amplifyUpdatePassword,
  fetchAuthSession
} from "@aws-amplify/auth";

export type Caller = {
  sub: string;
  email?: string;
  groups: string[];
  // Surfaced from the `custom:coachId` JWT claim when present. Used by
  // forms that should pre-fill the caller's own coach record (e.g.
  // DojoFormDialog locks the field for non-admin coaches).
  coachId?: string;
};

export type SignInResult =
  | { kind: "done" }
  // Cognito invite flow: user must set a permanent password before they can
  // continue. Frontend renders a new-password form, then calls
  // `confirmSignInWithNewPassword(newPassword)` to complete the challenge.
  | { kind: "new-password-required" };

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const res = await amplifySignIn({
    username: email,
    password,
    options: { authFlowType: "USER_PASSWORD_AUTH" }
  });
  if (res.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
    return { kind: "new-password-required" };
  }
  return { kind: "done" };
}

export async function confirmSignInWithNewPassword(newPassword: string): Promise<void> {
  await amplifyConfirmSignIn({ challengeResponse: newPassword });
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}

/**
 * Change the password for the currently signed-in user. Cognito
 * validates the old password and applies the pool's password policy
 * to the new one. Throws on policy mismatch / wrong old password —
 * the caller surfaces Amplify's error.message to the user.
 */
export async function changePassword(input: {
  oldPassword: string;
  newPassword: string;
}): Promise<void> {
  await amplifyUpdatePassword({
    oldPassword: input.oldPassword,
    newPassword: input.newPassword
  });
}

/**
 * Step 1 of the forgot-password flow. Cognito sends a verification
 * code to the user's email; the code is consumed by
 * confirmForgotPassword below.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await amplifyResetPassword({ username: email });
}

/**
 * Step 2 of the forgot-password flow. Submits the code + the new
 * password; on success the user can immediately sign in with the new
 * password.
 */
export async function confirmPasswordReset(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  await amplifyConfirmResetPassword({
    username: input.email,
    confirmationCode: input.code,
    newPassword: input.newPassword
  });
}

export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

// Mirrors apps/backend/src/shared/auth.ts#parseGroups — HTTP API v2 flattens
// `cognito:groups` into "[A B]" strings in some contexts, but the ID token
// consumed client-side returns a JSON array. Handle both defensively.
function parseGroups(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw !== "string") return [];
  const unwrapped = raw.trim().replace(/^\[|\]$/g, "");
  return unwrapped.split(/[\s,]+/).filter(Boolean);
}

export async function getCurrentCaller(): Promise<Caller | null> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
    if (!payload || typeof payload.sub !== "string") return null;
    return {
      sub: payload.sub,
      email: typeof payload.email === "string" ? payload.email : undefined,
      groups: parseGroups(payload["cognito:groups"]),
      coachId:
        typeof payload["custom:coachId"] === "string"
          ? (payload["custom:coachId"] as string)
          : undefined
    };
  } catch {
    return null;
  }
}
